// Relational GNN spatial candidate-ranking contract.
// The graph describes case + active hardware; candidate_features describe a complete
// proposed arrangement. Hard geometry remains authoritative and candidate_mask excludes
// physically invalid proposals from model selection.

export const SPATIAL_RANK_CONTRACT_VERSION = 1;
export const SPATIAL_RANK_MAX_CANDIDATES = 24;
export const SPATIAL_RANK_NODE_ORDER = Object.freeze(['case','mobo','gpu','psu','cooler','aio']);
export const SPATIAL_RANK_NODE_FEATURE_COUNT = 21;
export const SPATIAL_RANK_CASE_CONTEXT_COUNT = 20;
export const SPATIAL_RANK_CANDIDATE_FEATURE_COUNT = 64;

export const SPATIAL_RANK_ADJACENCY = Object.freeze([
  [0,1,1,1,1,1],
  [1,0,1,1,1,1],
  [1,1,0,1,1,1],
  [1,1,1,0,1,1],
  [1,1,1,1,0,0],
  [1,1,1,1,0,0]
]);

const FORM_FACTOR_IDS = Object.freeze({
  itx:1,dtx:2,matx:3,atx:4,sfx:5,sfx_l:6,'sfx-l':6,flex:7,flex_atx:7,custom:8
});
const LAYOUT_IDS = Object.freeze({
  reference:0,sandwich:1,console:2,bench:3,
  vertical_sandwich:4,vertical_reference:5,inverted_reference:6,vertical_inverted_reference:7,
  vertical_console:8,apu:9,apu_only:9
});

function finite(value,fallback=0){const n=Number(value);return Number.isFinite(n)?n:fallback;}
function clamp(value,min,max){return Math.max(min,Math.min(max,value));}
function normToken(value){return String(value||'').trim().toLowerCase().replace(/\s+/g,'_').replace(/-/g,'_');}
function ffId(value){return FORM_FACTOR_IDS[normToken(value)]||FORM_FACTOR_IDS.custom;}
function familyId(value){
  const raw=String(value||'reference').toLowerCase();
  if(raw.includes('sandwich'))return 1;
  if(raw.includes('console'))return 2;
  return 0;
}
function layoutId(value){
  const raw=normToken(value||'reference');
  if(LAYOUT_IDS[raw]!==undefined)return LAYOUT_IDS[raw];
  if(raw.includes('vertical')&&raw.includes('console'))return 8;
  if(raw.includes('vertical')&&raw.includes('sandwich'))return 4;
  if(raw.includes('vertical'))return 5;
  if(raw.includes('sandwich'))return 1;
  if(raw.includes('console'))return 2;
  return 0;
}
function oneHot(index,count){return Array.from({length:count},(_,i)=>i===index?1:0);}
function rotationIdentity(){return [1,0,0,0,1,0,0,0,1];}
function nodeVector({dims=[0,0,0],position=[0,0,0],rotation=null,socket=0,formFactor='',spine=0,layout='reference',flip=0,present=1}={}){
  const rot=Array.isArray(rotation)&&rotation.length===9?rotation:rotationIdentity();
  return [
    finite(dims[0]),finite(dims[1]),finite(dims[2]),
    finite(position[0]),finite(position[1]),finite(position[2]),
    ...rot.map(v=>finite(v)),finite(socket),ffId(formFactor),finite(spine),layoutId(layout),flip?1:0,present?1:0
  ];
}
function supportFlags(profile){
  const mobos=new Set((profile?.motherboardTypes||[]).map(v=>normToken(v)));
  const psus=new Set((profile?.psuTypes||[]).map(v=>normToken(v)));
  return [
    mobos.has('itx')?1:0,mobos.has('dtx')?1:0,mobos.has('matx')?1:0,mobos.has('atx')?1:0,
    psus.has('sfx')?1:0,(psus.has('sfx_l')||psus.has('sfx-l'))?1:0,psus.has('atx')?1:0,(psus.has('flex')||psus.has('flex_atx'))?1:0
  ];
}
function volumeLitres(caseObj){
  const explicit=finite(caseObj?.volume,0); if(explicit>0)return explicit;
  return finite(caseObj?.w,0)*finite(caseObj?.h,0)*finite(caseObj?.l,0)/1e6;
}
function caseContext(caseObj,effective,profile){
  const limits=effective?.limits||{};
  const [itx,dtx,matx,atx,sfx,sfxl,atxPsu,flex]=supportFlags(profile);
  const layout=effective?.layoutFamily||caseObj?.style||caseObj?.layout||'reference';
  return [
    layoutId(layout),finite(effective?.fixedStep?.spineX||0),0,0,volumeLitres(caseObj),
    itx,dtx,matx,atx,sfx,sfxl,atxPsu,flex,
    effective?.hasSpine?1:0,
    finite(limits.maxGPU),finite(limits.maxGPUH),finite(limits.maxGPUT),finite(limits.maxCPU),finite(limits.maxAIO),finite(limits.maxPSU)
  ];
}
function descriptorDims(desc){return [finite(desc?.dims?.l),finite(desc?.dims?.w),finite(desc?.dims?.h)];}
function graphFeatures(caseObj,effective,solved){
  const inv=solved?.inventory||{};
  const layout=effective?.layoutFamily||caseObj?.style||'reference';
  const spine=finite(solved?.best?.spineX||0);
  const nodes=[
    nodeVector({dims:[finite(caseObj?.w),finite(caseObj?.h),finite(caseObj?.l)],layout,spine,present:1,formFactor:'custom'}),
    nodeVector({dims:descriptorDims(inv.mobo),layout,spine,present:inv.mobo?1:0,formFactor:inv.mobo?.formFactor||'itx'}),
    nodeVector({dims:descriptorDims(inv.gpu),layout,spine,present:inv.gpu?1:0,formFactor:'custom'}),
    nodeVector({dims:descriptorDims(inv.psu),layout,spine,present:inv.psu?1:0,formFactor:inv.psu?.formFactor||'sfx'}),
    nodeVector({dims:descriptorDims(inv.cooler),layout,spine,present:inv.cooler?1:0,formFactor:'custom'}),
    nodeVector({dims:descriptorDims(inv.radiator),layout,spine,present:inv.radiator?1:0,formFactor:'custom'})
  ];
  return {component_specs:nodes,categorical_ids:[0,1,2,3,4,5],adjacency_matrix:SPATIAL_RANK_ADJACENCY,case_context:caseContext(caseObj,effective,solved?.capabilityProfile)};
}
function caseAxes(caseObj){return [Math.max(1,finite(caseObj?.w,1)),Math.max(1,finite(caseObj?.h,1)),Math.max(1,finite(caseObj?.l,1))];}
function candidateComponentFeatures(candidate,key,caseObj){
  const p=candidate?.primary?.[key];
  if(!p)return [0,0,0,0,0,0,0,0,0];
  const axes=caseAxes(caseObj); const pos=p.position||[0,0,0]; const size=p.expectedWorldSize||[0,0,0];
  const half=axes.map(v=>v/2);
  const boundary=Math.min(...[0,1,2].map(i=>Math.abs(half[i]-(Math.abs(finite(pos[i]))+finite(size[i])/2))/axes[i]));
  return [
    1,
    clamp(finite(pos[0])/axes[0],-1.5,1.5),clamp(finite(pos[1])/axes[1],-1.5,1.5),clamp(finite(pos[2])/axes[2],-1.5,1.5),
    clamp(finite(size[0])/axes[0],0,2),clamp(finite(size[1])/axes[1],0,2),clamp(finite(size[2])/axes[2],0,2),
    clamp(finite(p.orientationClass)/23,0,1),clamp(boundary,-1,1)
  ];
}
function center(candidate,key){const p=candidate?.primary?.[key]?.position;return Array.isArray(p)?p:[0,0,0];}
function distNorm(a,b,caseObj){const A=center(a.candidate,a.key),B=center(b.candidate,b.key);const axes=caseAxes(caseObj);return Math.sqrt(((A[0]-B[0])/axes[0])**2+((A[1]-B[1])/axes[1])**2+((A[2]-B[2])/axes[2])**2);}
function candidateVector(candidate,caseObj){
  const family=familyId(candidate?.family); const variant=String(candidate?.variant||'standard');
  const variantIndex=variant==='vertical'?1:variant==='inverted'?2:variant==='vertical_inverted'?3:0;
  const common=[...oneHot(family,3),...oneHot(variantIndex,4),candidate?.hardValid?1:0,clamp(finite(candidate?.hardPenalty)/200,0,3),clamp(finite(candidate?.spineX)/Math.max(1,finite(caseObj?.w,1)),-1,1)];
  const components=['motherboard','gpu','psu','cooler','radiator'].flatMap(key=>candidateComponentFeatures(candidate,key,caseObj));
  const rel=[
    distNorm({candidate,key:'motherboard'},{candidate,key:'gpu'},caseObj),
    distNorm({candidate,key:'gpu'},{candidate,key:'psu'},caseObj),
    distNorm({candidate,key:'motherboard'},{candidate,key:'psu'},caseObj),
    distNorm({candidate,key:'gpu'},{candidate,key:'radiator'},caseObj)
  ];
  const diag=candidate?.hardDiagnostics||{};
  const tail=[clamp(finite(diag.overflow)/200,0,3),clamp(finite(diag.collision),0,3),...rel];
  const out=[...common,...components,...tail];
  while(out.length<SPATIAL_RANK_CANDIDATE_FEATURE_COUNT)out.push(0);
  return out.slice(0,SPATIAL_RANK_CANDIDATE_FEATURE_COUNT);
}

export function buildSpatialRankPayload(caseObj,effective,solved){
  const graph=graphFeatures(caseObj,effective,solved);
  const candidates=(solved?.rankingCandidates||[]).slice(0,SPATIAL_RANK_MAX_CANDIDATES);
  const features=candidates.map(c=>candidateVector(c,caseObj));
  const mask=candidates.map(c=>c?.hardValid?1:0);
  while(features.length<SPATIAL_RANK_MAX_CANDIDATES){features.push(new Array(SPATIAL_RANK_CANDIDATE_FEATURE_COUNT).fill(0));mask.push(0);}
  return {
    contract_version:SPATIAL_RANK_CONTRACT_VERSION,
    ...graph,
    candidate_features:features,
    candidate_mask:mask,
    candidate_ids:candidates.map(c=>c.rankId),
    candidate_count:candidates.length
  };
}

export function spatialRankContractMetadata(){
  return {
    contract_version:SPATIAL_RANK_CONTRACT_VERSION,
    node_order:[...SPATIAL_RANK_NODE_ORDER],
    node_feature_count:SPATIAL_RANK_NODE_FEATURE_COUNT,
    case_context_count:SPATIAL_RANK_CASE_CONTEXT_COUNT,
    candidate_feature_count:SPATIAL_RANK_CANDIDATE_FEATURE_COUNT,
    max_candidates:SPATIAL_RANK_MAX_CANDIDATES
  };
}
